/**
 * FullscreenWrapper Component
 *
 * A reusable wrapper component that provides fullscreen functionality for any child component.
 * Features:
 * - Expand/collapse icon in top right corner
 * - Smooth transition animations
 * - Portal rendering for true fullscreen overlay
 * - ESC key to exit fullscreen
 */

import React, {useContext, useEffect, useRef, useState} from 'react';
import {Box, Fade, IconButton, Portal, useTheme} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

interface FullscreenContextValue {
    isFullscreen: boolean;
    setFullscreen: (value: boolean) => void;
}

/**
 * Exposes the nearest FullscreenWrapper's fullscreen state and a setter, so a child
 * can both read whether it is fullscreen and request entering/exiting it.
 * Defaults to a no-op for children rendered outside any wrapper.
 */
const FullscreenContext = React.createContext<FullscreenContextValue>({
    isFullscreen: false,
    setFullscreen: () => { /* no-op outside a wrapper */ },
});

/** Read whether the nearest FullscreenWrapper ancestor is currently fullscreen. */
export const useIsFullscreen = (): boolean => useContext(FullscreenContext).isFullscreen;

/** Access the nearest FullscreenWrapper's fullscreen state and setter. */
export const useFullscreenControls = (): FullscreenContextValue => useContext(FullscreenContext);

interface FullscreenWrapperProps {
    children: React.ReactNode;
    className?: string;
    defaultPadding?: number;
    backgroundColor?: string;
    zIndex?: number;
    /** When true, wrapper uses flex layout to fill parent container height */
    fillParent?: boolean;
    /**
     * Hide the default expand button in the normal (non-fullscreen) view. Use when the
     * wrapped child provides its own affordance to enter fullscreen (via useFullscreenControls).
     */
    hideExpandButton?: boolean;
}

const FullscreenWrapper: React.FC<FullscreenWrapperProps> = ({
    children,
    className,
    defaultPadding = 16,
    backgroundColor,
    zIndex = 1300, // Default MUI modal z-index
    fillParent = false,
    hideExpandButton = false,
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    // Handle ESC key to exit fullscreen
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false);
            }
        };

        if (isFullscreen) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent body scroll when in fullscreen
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isFullscreen]);

    const toggleFullscreen = (): void => {
        setIsFullscreen(!isFullscreen);
    };

    const fullscreenContent = (
        <Box
            ref={containerRef}
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: backgroundColor || theme.palette.background.paper,
                zIndex,
                display: 'flex',
                flexDirection: 'column',
                padding: defaultPadding / 8, // Convert to theme spacing units
            }}
        >
            {/* Fullscreen controls */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    zIndex: zIndex + 1,
                }}
            >
                <IconButton
                    onClick={toggleFullscreen}
                    size="large"
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: theme.shadows[2],
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                    title="Exit fullscreen (ESC)"
                >
                    <FullscreenExitIcon />
                </IconButton>
            </Box>

            {/* Child component in fullscreen */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                height: '100%',
                ...(fillParent && {
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }),
            }}>
                {children}
            </Box>
        </Box>
    );

    const normalContent = (
        <Box
            className={className}
            sx={{
                position: 'relative',
                height: '100%',
                width: '100%',
                ...(fillParent && {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                }),
            }}
        >
            {/* Expand button (hidden when the child provides its own affordance) */}
            {!hideExpandButton && (
                <IconButton
                    onClick={toggleFullscreen}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: theme.shadows[1],
                        opacity: 0.8,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                            opacity: 1,
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                    title="Enter fullscreen"
                >
                    <FullscreenIcon fontSize="small" />
                </IconButton>
            )}

            {/* Child component in normal view */}
            {children}
        </Box>
    );

    return (
        <FullscreenContext.Provider value={{ isFullscreen, setFullscreen: setIsFullscreen }}>
            {/* Normal view */}
            {!isFullscreen && normalContent}

            {/* Fullscreen view */}
            {isFullscreen && (
                <Portal>
                    <Fade in={isFullscreen} timeout={300}>
                        {fullscreenContent}
                    </Fade>
                </Portal>
            )}
        </FullscreenContext.Provider>
    );
};

export default FullscreenWrapper;